/**
 * modelState — what the nightly model batch last judged for each portfolio
 * (WORK.md item 57 step 6), read from `/api/model-state` (vip-store's
 * `model_state` rows). The batch writes the rows; the app only reads them,
 * once per app load, into `rebalanceRuns.ts`'s cache, where the Projections
 * dot and the Model tab both read them.
 *
 * Three answers, never two: the rows, "the batch has not run" (the owner has
 * not applied the migration, said in the route's own words), or a failed
 * read. A failed read throws; it is never an empty list, which would read as
 * "checked, nothing to say".
 *
 * WHAT `checkedAt` IS. The time the row was last WRITTEN, not the last night
 * that checked the portfolio: the batch rewrites a row only when something in
 * it other than the clock changed (`model_batch.py`'s CLOCK_FIELDS), so a
 * night that finds the same verdict leaves the row as it was. The weekly
 * heartbeat moves the row's `next_heartbeat`, so a running batch re-records
 * every row at least every HEARTBEAT_DAYS, whatever its verdict; a row older
 * than that by more than a day means the batch has stopped checking it. So
 * the words say when a verdict was RECORDED, never when it was last checked,
 * and call a row that old overdue.
 */
import { z } from 'zod';

const RowSchema = z.object({
  portfolioId: z.string(),
  verdict: z.enum(['rebalance', 'within_tolerance', 'could_not_run', 'no_model']),
  checkedAt: z.number(),
  lastRunAt: z.number().nullable(),
  lastRunId: z.string().nullable(),
  nextWake: z.string(),
  wakeReason: z.string(),
  /** The batch's summary: held_by, flags, trigger, reason. */
  summary: z.object({
    held_by: z.array(z.string()).optional(),
    flags: z.array(z.string()).optional(),
    trigger: z.enum(['return', 'risk', 'both']).nullable().optional(),
    reason: z.string().nullable().optional(),
  }).loose().nullable(),
});
export type ModelStateRow = z.infer<typeof RowSchema>;

export const ModelStateResponseSchema = z.union([
  z.object({ ready: z.literal(true), rows: z.array(RowSchema) }),
  z.object({ ready: z.literal(false), reason: z.string(), rows: z.array(RowSchema).length(0) }),
]);
export type ModelStateResponse = z.infer<typeof ModelStateResponseSchema>;

export async function fetchModelState(fetcher: typeof fetch = fetch): Promise<ModelStateResponse> {
  const res = await fetcher('/api/model-state', { credentials: 'include' });
  if (!res.ok) throw new Error(`model-state read failed (HTTP ${res.status})`);
  const parsed = ModelStateResponseSchema.safeParse(await res.json());
  if (!parsed.success) {
    throw new Error(`model-state answer is not the route's shape: ${parsed.error.issues[0]?.message}`);
  }
  return parsed.data;
}

/**
 * `tasks/model_batch/batch_schedule.py`'s HEARTBEAT_DAYS: a running batch
 * re-records every row at least this often. tasks/tests/test_model_batch.py
 * holds the two equal.
 */
export const MODEL_HEARTBEAT_DAYS = 7;
const DAY_MS = 86_400_000;

/** Has the batch left the row unrecorded for more than HEARTBEAT_DAYS + 1 days? */
export function isModelRowOverdue(row: ModelStateRow, now: number): boolean {
  return now - row.checkedAt > (MODEL_HEARTBEAT_DAYS + 1) * DAY_MS;
}

const day = (ms: number) => new Date(ms).toISOString().slice(0, 10);

function half(row: ModelStateRow): string | null {
  const t = row.summary?.trigger;
  return t === 'both' ? 'on return and risk' : t === 'risk' ? 'on risk' : t === 'return' ? 'on return' : null;
}

function daysUnrecorded(row: ModelStateRow, now: number): number {
  return Math.floor((now - row.checkedAt) / DAY_MS);
}

/**
 * The dot's words for a nightly row whose verdict draws it (`rebalance`):
 * that it is the nightly check's verdict, the half that tripped, the date the
 * verdict was recorded, and, once the row is older than a running batch
 * leaves one, that the check is overdue.
 */
export function nightlyBadgeLabel(row: ModelStateRow, now: number): string {
  const h = half(row);
  return `Rebalance suggested by the nightly model check (recorded ${day(row.checkedAt)})${h ? `: ${h}` : ''}`
    + (isModelRowOverdue(row, now)
      ? `. The nightly check is overdue: nothing recorded for ${daysUnrecorded(row, now)} days`
      : '');
}

/**
 * The Model tab's line for a row, whatever its verdict: the nightly check's
 * verdict and the date it was recorded, why it could not run when it could
 * not, and the overdue note when it applies. A check that could not run is
 * said here, in the app, and not only in the batch's log.
 */
export function nightlyLine(row: ModelStateRow, now: number): string {
  const on = `(recorded ${day(row.checkedAt)})`;
  const h = half(row);
  const said = row.verdict === 'rebalance'
    ? `The nightly model check suggested a rebalance${h ? `, ${h}` : ''} ${on}.`
    : row.verdict === 'within_tolerance'
      ? `The nightly model check suggested no rebalance ${on}.`
      : row.verdict === 'could_not_run'
        ? `The nightly model check could not run ${on}: ${(row.summary?.reason ?? 'no reason recorded').replace(/[.\s]+$/, '')}.`
        : `The nightly model check found no model chosen ${on}.`;
  return said + (isModelRowOverdue(row, now)
    ? ` It is overdue: nothing has been recorded for ${daysUnrecorded(row, now)} days, and a running check records every portfolio at least every ${MODEL_HEARTBEAT_DAYS} days.`
    : '');
}
